<template>
  <div
    :class="[!!receipt.CanceledAt ? 'red--text' : '']"
    class="divTableRow text-caption"
  >
    <div
      :class="[receiptTypeColor(receipt, true)]"
      class="divTableCell text-left text-no-wrap"
    >
      {{ receipt.InvoiceTypeCode }} {{ receipt.InvoiceTypeName }}
    </div>

    <div
      v-if="!!orders.length"
      class="divTableCell text-center text-no-wrap"
    >
      <div>
        {{ tableReceiptOrderNumber(receipt, orders) }}
      </div>
    </div>

    <div class="divTableCell text-right text-no-wrap">
      <div class="text-no-wrap">
        <v-chip
          :color="receiptTypeColor(receipt)"
          class="align-center justify-center font-weight-bold px-1"
          dark
          label
          style="min-width: 90px;"
          x-small
          @click="copyToClipboard(receipt.InternalId)"
        >
          {{ receipt.InternalId }}
        </v-chip>

        <v-btn
          v-if="receipt.DocumentUrl"
          :color="receiptTypeColor(receipt)"
          :href="receipt.DocumentUrl"
          icon
          target="_blank"
          x-small
        >
          <v-icon>mdi-open-in-new</v-icon>
        </v-btn>

        <div
          v-else
          class="d-inline-flex"
          style="width: 18px; height: 18px;"
        />
      </div>
    </div>

    <div class="divTableCell text-right text-no-wrap">
      <v-tooltip
        :color="isDarkMode ? '#191919' : 'grey lighten-5'"
        :disabled="!receipt.PaymentMethods || (receipt.PaymentMethods && !receipt.PaymentMethods.length)"
        bottom
      >
        <template #activator="{on}">
          <div v-on="on">
            <v-badge
              color="transparent"
            >
              {{ receipt.Amount | currency }}

              <template
                v-if="receipt.PaymentMethods && receipt.PaymentMethods.length"
                #badge
              >
                <v-icon
                  color="info"
                  x-small
                >
                  mdi-information-variant-circle-outline
                </v-icon>
              </template>
            </v-badge>
          </div>
        </template>

        <v-card
          color="transparent"
          flat
        >
          <v-card-title class="justify-center font-weight-bold text-body-1">
            {{ $tc('Restaurant.Tables.Dialog.Payment.Title', 0) }}
          </v-card-title>

          <v-list
            color="transparent"
            dense
            flat
          >
            <v-list-item
              v-for="(payment, paymentIdx) in receipt.PaymentMethods"
              :key="paymentIdx"
              class="text-left"
            >
              <v-list-item-avatar :color="getPaymentTypeLangByCode(payment.Code).Color">
                <v-icon
                  dark
                  v-text="getPaymentTypeLangByCode(payment.Code).Icon"
                />
              </v-list-item-avatar>

              <v-list-item-content>
                <v-list-item-title>
                  {{ getPaymentTypeLangByCode(payment.Code).Title }}
                </v-list-item-title>
                <v-list-item-subtitle>
                  {{ payment.Amount | currency }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card>
      </v-tooltip>
    </div>

    <div
      v-if="appHasFiscalDocsProvider && appFiscalDocsProviderType === FiscalDocsProviderTypeEnum.CLOUD"
      class="divTableCell text-center text-no-wrap"
      style="cursor: pointer;"
    >
      <template v-if="receipt.Mark">
        <v-tooltip
          :color="isDarkMode ? '#191919' : 'grey lighten-5'"
          bottom
        >
          <template #activator="{on}">
            <div
              @click="copyToClipboard(receipt.Mark)"
              v-on="on"
            >
              <v-badge
                color="transparent"
                offset-x="6"
                offset-y="16"
                overlap
              >
                <my-data-logo
                  class="ma-1"
                  img-class="pa-1"
                  width="55"
                />

                <template #badge>
                  <v-icon
                    color="info"
                    x-small
                  >
                    mdi-information-variant-circle-outline
                  </v-icon>
                </template>
              </v-badge>
            </div>
          </template>

          <div
            :class="isDarkMode ? 'white--text' : 'black--text'"
            class="text-caption pa-2 text-center"
          >
            <my-data-logo
              class="mb-2"
              img-class="py-1 px-3"
              width="125"
            />

            <div class="font-weight-medium">
              {{ $t('Order.OrderDetailsDialog.Receipt.Submitted') }}
            </div>

            <div class="my-4" />

            <div class="text-left">
              <div>{{ $t('Order.OrderDetailsDialog.Receipt.Mark') }}</div>
              <div class="grey--text">
                {{ receipt.Mark }}
              </div>

              <div class="my-2" />

              <div>{{ $t('Order.OrderDetailsDialog.Receipt.Uid') }}</div>
              <div class="grey--text">
                {{ receipt.Uid }}
              </div>

              <div class="my-2" />

              <div>{{ $t('Order.OrderDetailsDialog.Receipt.AuthCode') }}</div>
              <div class="grey--text">
                {{ receipt.AuthenticationCode }}
              </div>

              <div class="my-2" />

              <div>{{ $t('Order.OrderDetailsDialog.Receipt.Provider') }}</div>
              <div class="grey--text">
                {{ receipt.Provider }}
              </div>
            </div>
          </div>
        </v-tooltip>
      </template>
    </div>

    <div class="divTableCell text-center text-no-wrap">
      <v-tooltip
        :color="receiptStatus(receipt).Color"
        bottom
        max-width="400"
      >
        <template #activator="{on}">
          <v-chip
            :color="receiptStatus(receipt).Color"
            class="align-center justify-center font-weight-bold"
            dark
            label
            style="min-width: 100px;"
            x-small
            v-on="on"
          >
            {{ receiptStatus(receipt).Name.toLocaleUpperCase($i18n.locale) }}
          </v-chip>
        </template>

        <template v-if="(receiptHasError(receipt) || receipt?.CanRetry) && receiptErrors(receipt).length">
          <div class="text-center">
            <h4 class="mb-2">
              {{ receipt.InvoiceTypeCode }} {{ receipt.InvoiceTypeName }}
            </h4>

            <template v-for="(error, index) in receiptErrors(receipt)">
              <div
                :key="index"
                class="text-left mb-1"
              >
                <span class="font-weight-bold">{{ error.code }}</span> - {{ error.message }}
              </div>
            </template>
          </div>
        </template>

        <template v-else>
          <div class="text-center">
            <h4 class="mb-2">
              {{ receipt.InvoiceTypeCode }} {{ receipt.InvoiceTypeName }}
            </h4>

            <div class="font-weight-bold">
              {{ receiptStatus(receipt).Name.toLocaleUpperCase($i18n.locale) }}
            </div>
          </div>
        </template>
      </v-tooltip>
    </div>

    <div class="divTableCell text-right text-no-wrap">
      <template v-for="(item, itemIdx) in receipt.relatedItems">
        <div
          :key="`receipt-related-item-${itemIdx}`"
          class="text-no-wrap"
        >
          <v-chip
            :color="receiptTypeColor(item)"
            class="align-center justify-center font-weight-bold px-1"
            dark
            label
            style="min-width: 90px;"
            x-small
            @click="copyToClipboard(receipt.InternalId)"
          >
            {{ item.InternalId }}
          </v-chip>

          <v-btn
            v-if="item.DocumentUrl"
            :color="receiptTypeColor(item)"
            :href="item.DocumentUrl"
            icon
            target="_blank"
            x-small
          >
            <v-icon>mdi-open-in-new</v-icon>
          </v-btn>
        </div>
      </template>
    </div>

    <div class="divTableCell text-center">
      <receipt-notify-details :receipt="receipt" />
    </div>

    <div
      class="divTableCell text-right text-no-wrap"
      style="line-height: 14px;"
    >
      <div class="text-no-wrap d-flex align-center justify-center">
        <display-date-time
          :date-time="receipt.CreatedAt"
          class="mr-2"
        />

        <user-chip
          v-if="receipt.UserId > 0"
          :color="receipt.UserColor"
          :full-name="receipt.User"
          :short-name="receipt.UserShortName"
        />
      </div>
    </div>

    <div class="divTableCell text-right pa-1">
      <v-speed-dial
        v-if="!receiptHasError(receipt)"
        v-model="receipt.fab"
        direction="left"
        transition="slide-x-reverse-transition"
      >
        <template #activator>
          <v-btn
            icon
            small
          >
            <v-icon>mdi mdi-dots-vertical</v-icon>
          </v-btn>
        </template>

        <print-receipt-button
          v-if="userCanPrintFiscalDoc && print && posUser?.Settings?.AllUsers?.Permissions?.CanPrintOrderFiscal && !receiptHasError(receipt)"
          :small-size="28"
          class="ml-2"
          hide-label
          icon
          small
          small-icon
          @fiscal-document:print="$bus.$emit('fiscal-document:print', receipt.Id)"
        />

        <email-receipt-button
          v-if="userCanViewFiscalDoc && email && !receiptHasError(receipt)"
          :small-size="28"
          class="ml-2"
          hide-label
          icon
          small
          small-icon
          @fiscal-document:email="$bus.$emit('fiscal-document:email', receipt.Id, order || table, {email: email || null, From: order ? EmailNotifyFromEnum.ORDER : table ? EmailNotifyFromEnum.TABLE : EmailNotifyFromEnum.RECEIPT})"
        />

        <template v-if="userCanViewFiscalDoc && appHasFiscalDocsProvider && receipt.DocumentUrl && !receiptHasError(receipt)">
          <v-tooltip
            color="light-green"
            top
          >
            <template #activator="{on}">
              <v-btn
                class="light-green ma-0 ml-2"
                dark
                icon
                small
                @click="$bus.$emit('fiscal-document:view', receipt.DocumentUrl)"
                v-on="on"
              >
                <v-icon small>
                  mdi-open-in-new
                </v-icon>
              </v-btn>
            </template>

            <span>{{ $t('Common.Button.ViewDocument') }}</span>
          </v-tooltip>
        </template>

        <template v-if="canRetryReceipt(receipt)">
          <v-tooltip
            color="orange"
            top
          >
            <template #activator="{on}">
              <v-btn
                class="orange ma-0 ml-2"
                dark
                icon
                small
                @click="$bus.$emit('fiscal-document:retry', receipt.Id)"
                v-on="on"
              >
                <v-icon small>
                  mdi-cloud-upload-outline
                </v-icon>
              </v-btn>
            </template>

            <span>{{ $t('Common.Button.RetryDocument') }}</span>
          </v-tooltip>
        </template>

        <template v-if="canConvertReceipt(receipt)">
          <v-tooltip
            :color="$t('Order.OrdersAll.ListView.Button.Convert.Color')"
            top
          >
            <template #activator="{on}">
              <v-btn
                :class="$t('Order.OrdersAll.ListView.Button.Convert.Color')"
                class="ma-0 ml-2"
                dark
                icon
                small
                @click="onCovertReceiptToInvoice(receipt, table)"
                v-on="on"
              >
                <v-icon small>
                  {{ $t('Order.OrdersAll.ListView.Button.Convert.Icon') }}
                </v-icon>
              </v-btn>
            </template>

            <span>{{ $t('Order.OrderDetailsDialog.Receipt.Convert') }}</span>
          </v-tooltip>
        </template>

        <template v-if="canCancelReceipt(receipt)">
          <v-tooltip
            :color="$t('Order.OrdersAll.ListView.Button.Delete.Color')"
            top
          >
            <template #activator="{on}">
              <v-btn
                :class="$t('Order.OrdersAll.ListView.Button.Delete.Color')"
                class="ma-0 ml-2"
                dark
                icon
                small
                @click="cancelDocument(receipt, table)"
                v-on="on"
              >
                <v-icon small>
                  {{ $t('Order.OrdersAll.ListView.Button.Cancel.Icon') }}
                </v-icon>
              </v-btn>
            </template>

            <span>{{ $t('Order.OrderDetailsDialog.Receipt.Cancel') }}</span>
          </v-tooltip>
        </template>
      </v-speed-dial>
    </div>
  </div>
</template>

<script>

import MyDataLogo from '@/components/receipts/MyDataLogo.vue'
import EmailReceiptButton from '@/components/common/receipt/EmailReceiptButton.vue'
import PrintReceiptButton from '@/components/common/receipt/PrintReceiptButton.vue'
import Auth from '@/mixins/auth'
import AppData from '@/mixins/appdata'
import ReceiptsCommon from '@/mixins/receipts/receiptsCommon'
import PaymentType from '@/mixins/orders/paymentType'
import PinManagerEnum from '@/api/Enums/PinManagerEnum.js'
import {getContrastColor} from '@/lib/color/index.js'
import ReceiptNotifyDetails from '@/components/receipts/ReceiptNotifyDetails.vue'
import EmailNotifyFromEnum from '@/api/Enums/EmailNotifyFromEnum.js'
import FiscalDocsProviderTypeEnum from '@/api/Enums/FiscalDocsProviderTypeEnum'
import DisplayDateTime from '@/components/common/DisplayDateTime.vue'
import UserChip from '@/components/common/UserChip.vue'

export default {
  name      : 'ReceiptsTableRow',
  components: {
    UserChip,
    DisplayDateTime,
    ReceiptNotifyDetails,
    PrintReceiptButton,
    EmailReceiptButton,
    MyDataLogo
  },
  directives: {},
  mixins    : [Auth, AppData, PaymentType, ReceiptsCommon],
  props     : {
    receipt: {
      type   : Object,
      default: undefined
    },

    receiptIndex: {
      type   : Number,
      default: 0
    },

    print: {
      type   : Boolean,
      default: false
    },

    email: {
      type   : Boolean,
      default: false
    },

    cancel: {
      type   : Boolean,
      default: false
    },

    convert: {
      type   : Boolean,
      default: false
    },

    table: {
      type   : Object,
      default: undefined
    },

    order: {
      type   : Object,
      default: undefined
    },

    orders: {
      type   : Array,
      default: () => []
    },

    noBorder: {
      type   : Boolean,
      default: false
    },

    noPadding: {
      type   : Boolean,
      default: false
    }
  },
  data() {
    return {}
  },
  computed: {
    EmailNotifyFromEnum() {
      return EmailNotifyFromEnum
    },
    FiscalDocsProviderTypeEnum() {
      return FiscalDocsProviderTypeEnum
    },

    ordersToday() {
      const orders = this.$DataStore?.ordersDataToday?.items || []
      return orders.filter(order => parseInt(order.StatusId) > 0)
    }
  },
  watch: {},
  beforeCreate() {
  },
  created() {
  },
  beforeMount() {
  },
  mounted() {
  },
  beforeUpdate() {
  },
  updated() {
  },
  beforeDestroy() {
  },
  destroyed() {
  },
  methods: {
    getContrastColor,

    isOrderToday(order) {
      return !!this.ordersToday.find(o => parseInt(o.Id) === parseInt(order.Id))
    },

    onCovertReceiptToInvoice(receipt, table) {
      // Emit event for table receipt
      if (receipt && receipt?.Id && table && table?.Id) {
        this.$bus.$emit('fiscal-document:convert-to-invoice', {ReceiptId: receipt.Id, TableId: table.Id})
        return
      }

      // Emit event for order receipt
      if (receipt && receipt.Id) {
        this.$bus.$emit('fiscal-document:convert-to-invoice', {ReceiptId: receipt.Id}, {DocId: receipt.Id})
      }
    },

    cancelDocument(receipt, table) {
      this.executeWithPinPermissionManager(PinManagerEnum.DocumentCancel, (success) => {
        if (!success) return

        this.$bus.$emit('fiscal-document:cancel', {...receipt, ReceiptId: receipt.Id, TableId: table?.Id || null, OrderId: this.order?.Id || null}, {refund: false})
      })
    }
  }
}
</script>

<style scoped>
:deep(.v-speed-dial__list) {
  padding: 0;
}

.divTableCell.no-border {
  border-bottom: 0 !important;
}

.divTableCell.no-padding {
  padding-top: 2px !important;
  padding-bottom: 2px !important;
}
</style>
